import React from 'react';
import { Alert, Box, Columns, FieldStack, InputField, Spinner, styled } from 'bumbag';
import { Field } from 'formik';
import _constant from 'lodash/constant';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import _times from 'lodash/times';
import Card from 'react-health-card';
import Loads from 'react-loads';
import validateMedicareNumber from 'validate-medicare-number';

import { FormsCalendarInput as SingleDateField } from '@medipass/react-core/redux-form';
import parseHealthFundCardFields from '@medipass/utils/parse-health-fund-card-fields';
import validate, {
  isFutureDate,
  isLengthGreaterThanOrEqualTo,
  isLengthLessThanOrEqualTo,
  isRequired,
  isValidDate,
  matchesRegex,
} from '@medipass/utils/validate-form';
import sdk from '@medipass/web-sdk';

import parseStyle from 'legacy/utils/parse-style';

import 'react-health-card/lib/card.css';

const _getDateProperty = ({ day, month, year, type = 'mask' }: Record<string, any>) => {
  const isMask = type === 'mask';
  // @ts-expect-error TS(7034): Variable 'property' implicitly has type 'any[]' in... Remove this comment to see the full error message
  let property = [];

  if (day) {
    // @ts-expect-error TS(7005): Variable 'property' implicitly has an 'any[]' type... Remove this comment to see the full error message
    property = [...property, _times(day.minLength, _constant(isMask ? '9' : 'D')).join('')];
  }

  if (month) {
    // @ts-expect-error TS(7005): Variable 'property' implicitly has an 'any[]' type... Remove this comment to see the full error message
    property = [...property, _times(month.minLength, _constant(isMask ? '9' : 'M')).join('')];
  }

  if (year) {
    // @ts-expect-error TS(7005): Variable 'property' implicitly has an 'any[]' type... Remove this comment to see the full error message
    property = [...property, _times(year.minLength, _constant(isMask ? '9' : 'Y')).join('')];
  }

  // @ts-expect-error TS(7005): Variable 'property' implicitly has an 'any[]' type... Remove this comment to see the full error message
  return property.join('/');
};

const noop = () => undefined;

// @ts-expect-error TS(7006): Parameter 'value' implicitly has an 'any' type.
const validateCardNumber = value =>
  value && validateMedicareNumber(value) ? undefined : 'Medicare card number is not valid';

const CardWrapper = styled(Box)`
  display: flex;
  justify-content: center;
  margin-top: 1.5rem;

  @media screen and (max-width: 767px) {
    & .react-health-card__container {
      font-size: 10px;
      width: 250px;
      height: 160px;
    }

    & .react-health-card {
      min-width: 250px;
    }
  }
`;
type Props = {
  cardholder?: string | null | undefined;
  cardIssueNumber?: string | null | undefined;
  cardNumber?: string | null | undefined;
  expiryDate?: string | null | undefined;
  fieldNamePrefix?: string | null | undefined;
  healthFundId: string;
  isDependant?: boolean;
  isSelfCheckoutSession?: boolean;
  issueDate?: string | null | undefined;
  membershipNumber?: string | null | undefined;
  cardRank?: string | null | undefined;
  showFieldsIfUnsupported?: boolean;
  showDOB?: boolean;
  showName?: boolean;
  unsupportedMessage?: string;
  showVerifyFields?: boolean;
};
type State = {
  cardFields: any;
  focused: string | null | undefined;
};
export default class WithHealthFundCardFields extends React.Component<Props, State> {
  static defaultProps = {
    cardholder: null,
    cardIssueNumber: null,
    cardNumber: null,
    expiryDate: null,
    fieldNamePrefix: null,
    isDependant: false,
    isSelfCheckoutSession: false,
    issueDate: null,
    membershipNumber: null,
    cardRank: null,
    showFieldsIfUnsupported: true,
    showDOB: false,
    showName: false,
    unsupportedMessage:
      'This health fund has not yet enabled digital claiming. You can still add your card, and we’ll notify you when they are enabled.',
    showVerifyFields: true,
  };
  state: any = {
    cardFields: {},
    focused: null,
  };
  getHealthFund = async () => {
    const { healthFundId, isSelfCheckoutSession } = this.props;
    const healthFund = await sdk.healthfunds.getHealthFund(
      healthFundId,
      isSelfCheckoutSession
        ? {
            requestConfig: {
              tokenType: 'session',
            },
          }
        : {},
    );
    this.setCardFields(healthFund);
    return healthFund;
  };
  buildFieldName = (name: string) => {
    const { fieldNamePrefix } = this.props;
    return fieldNamePrefix ? `${fieldNamePrefix}.${name}` : name;
  };
  setCardFields = (healthFund: Record<string, any>) => {
    const cardFields = parseHealthFundCardFields(_get(healthFund, 'cardFields', []));
    this.setState({
      cardFields,
    });
  };
  render = () => {
    const {
      cardholder,
      cardNumber,
      expiryDate,
      healthFundId,
      isDependant,
      issueDate,
      cardIssueNumber,
      membershipNumber,
      cardRank,
      showFieldsIfUnsupported,
      showDOB,
      showName,
      unsupportedMessage,
      showVerifyFields,
    } = this.props;
    const { cardFields, focused } = this.state;

    const nameColor: string = _get(parseStyle(_get(cardFields, 'cardRank.cardStyle')), 'color');

    const date = issueDate || expiryDate;
    const parsedDate = date && date.split('/').join('').split(' ').join('');
    const day = cardFields.issueDay || cardFields.expiryDay;
    const month = cardFields.issueMonth || cardFields.expiryMonth;
    const year = cardFields.issueYear || cardFields.expiryYear;
    return (
      <Loads contextKey={`healthFund.${healthFundId}`} load={this.getHealthFund} loadOnMount>
        <Loads.Loading>
          <Spinner />
        </Loads.Loading>
        <Loads.Success>
          {/* @ts-expect-error TS(7031): Binding element 'healthFund' implicitly has an 'an... Remove this comment to see the full error message */}
          {({ response: healthFund }) => {
            const isBupa: boolean = _get(healthFund, 'code') === 'BUP';
            const shouldShowVerifyField = showVerifyFields && isBupa;
            const isMedicare: boolean = _get(healthFund, 'code') === 'MDC';
            return (
              <Box>
                {healthFund && (
                  <Box>
                    {healthFund.isUnsupported && (
                      <Alert variant="tint" marginTop="major-2" type="warning" title="Please note">
                        {unsupportedMessage}
                      </Alert>
                    )}
                    {(!healthFund.isUnsupported || showFieldsIfUnsupported) && (
                      <React.Fragment>
                        <CardWrapper>
                          <Card
                            logoUri={_get(healthFund, 'cardLogoUrl') || _get(healthFund, 'logoSmallUrl')}
                            bgImageFront={isBupa ? _get(healthFund, 'cardImageUrl') : undefined}
                            bgColorFront={_get(healthFund, 'cardBgColorFront') || _get(healthFund, 'primaryColor')}
                            bgColorBack={_get(healthFund, 'cardBgColorBack') || '#f7f7f7'}
                            nameStyle={`color: ${
                              nameColor && _get(cardFields, 'cardRank.cardPosition') === 'front' ? nameColor : 'white'
                            }`}
                            cardNumber={cardNumber && cardNumber.split(' ').join('')}
                            cardNumberTitle={_get(cardFields, 'cardNumber.displayName', null)}
                            cardNumberLength={
                              !_isEmpty(cardNumber) ? null : _get(cardFields, 'cardNumber.maxLength', null)
                            }
                            cardNumberPosition={_get(cardFields, 'cardNumber.cardPosition')}
                            cardNumberStyle={_get(cardFields, 'cardNumber.cardStyle')}
                            issueDate={parsedDate || ''}
                            issueDateFormat={_getDateProperty({
                              day,
                              month,
                              year,
                              type: 'format',
                            })}
                            issueDateStyle={
                              _get(cardFields, 'issueMonth.cardStyle', null) ||
                              _get(cardFields, 'expiryMonth.cardStyle', null)
                            }
                            issueDateTitle={issueDate ? 'Issue date' : 'Expiry date'}
                            issueNumber={cardIssueNumber && cardIssueNumber.split(' ').join('')}
                            issueNumberTitle={isBupa ? '' : _get(cardFields, 'cardIssueNumber.displayName', null)}
                            issueNumberLength={
                              !_isEmpty(cardIssueNumber) ? null : _get(cardFields, 'cardIssueNumber.maxLength', null)
                            }
                            issueNumberPosition={_get(cardFields, 'cardIssueNumber.cardPosition')}
                            issueNumberStyle={_get(cardFields, 'cardIssueNumber.cardStyle')}
                            rank={cardRank && cardRank.split(' ').join('')}
                            rankLength={!_isEmpty(cardRank) ? null : _get(cardFields, 'cardRank.maxLength', null)}
                            rankTitle={_get(cardFields, 'cardRank.displayName', null)}
                            rankPosition={_get(cardFields, 'cardRank.cardPosition')}
                            rankStyle={_get(cardFields, 'cardRank.cardStyle')}
                            memberNumber={membershipNumber && membershipNumber.split(' ').join('')}
                            memberNumberTitle={_get(cardFields, 'membershipNumber.displayName', null)}
                            memberNumberLength={
                              !_isEmpty(membershipNumber) ? null : _get(cardFields, 'membershipNumber.maxLength', null)
                            }
                            memberNumberPosition={_get(cardFields, 'membershipNumber.cardPosition')}
                            memberNumberStyle={_get(cardFields, 'membershipNumber.cardStyle')}
                            name={cardholder}
                            focused={focused}
                            showCardNumber={Boolean(cardFields.cardNumber)}
                            showIssueDate={Boolean(day || month || year)}
                            showIssueNumber={Boolean(cardFields.cardIssueNumber)}
                            showMemberNumber={Boolean(cardFields.membershipNumber)}
                            showRank={Boolean(cardFields.cardRank)}
                            showName={Boolean(healthFund)}
                          />
                        </CardWrapper>
                        <FieldStack marginTop="major-2">
                          {showName && (
                            <FieldStack orientation="horizontal">
                              <Field
                                component={InputField.Formik}
                                name={this.buildFieldName('firstName')}
                                label="First name"
                                inputProps={{
                                  autoComplete: 'given-name',
                                }}
                                validate={validate(isRequired)}
                              />
                              <Field
                                component={InputField.Formik}
                                name={this.buildFieldName('lastName')}
                                label="Last name"
                                inputProps={{
                                  autoComplete: 'family-name',
                                }}
                                validate={validate(isRequired)}
                              />
                            </FieldStack>
                          )}
                          {(showDOB || shouldShowVerifyField) && (
                            <Field
                              component={InputField.Formik}
                              name={this.buildFieldName('dob')}
                              label="Date of birth (DD/MM/YYYY)"
                              mask="99/99/9999"
                              inputProps={{
                                autoComplete: 'dob',
                                type: 'tel',
                              }}
                              validate={validate(isRequired, isValidDate, isFutureDate)}
                            />
                          )}
                          {!shouldShowVerifyField && cardFields.cardNumber && (
                            <Field
                              component={InputField.Formik}
                              disabled={isDependant}
                              maxLength={cardFields.cardNumber.maxLength}
                              onFocus={() =>
                                this.setState({
                                  focused: 'cardNumber',
                                })
                              }
                              type={cardFields.cardNumber.inputType === 'numeric' ? 'tel' : 'text'}
                              name={this.buildFieldName('cardNumber')}
                              label={`${_get(cardFields, 'cardNumber.displayName') || 'Card number'} ${
                                !isRequired && !cardFields?.cardNumber?.mandatory ? '(optional)' : ''
                              }`}
                              validate={validate(
                                cardFields.cardNumber.mandatory ? isRequired : noop,
                                matchesRegex(
                                  _get(cardFields, 'cardNumber.validation.regex'),
                                  _get(cardFields, 'cardNumber.validation.message'),
                                ),
                              )}
                            />
                          )}
                          {/* @ts-expect-error TS(2322): Type '"0"' is not assignable to type 'Flexible<Spa... Remove this comment to see the full error message */}
                          <Columns minBreakpoint="mobile" marginBottom="0">
                            {cardFields.membershipNumber && (
                              <Columns.Column spread={7}>
                                <Field
                                  component={InputField.Formik}
                                  disabled={isDependant}
                                  maxLength={cardFields.membershipNumber.maxLength}
                                  onFocus={() =>
                                    this.setState({
                                      focused: 'memberNumber',
                                    })
                                  }
                                  type={cardFields.membershipNumber.inputType === 'numeric' ? 'tel' : 'text'}
                                  name={this.buildFieldName('membershipNumber')}
                                  label={`${_get(cardFields, 'membershipNumber.displayName') || 'Membership no.'} ${
                                    !isRequired && !cardFields?.membershipNumber?.mandatory ? '(optional)' : ''
                                  }`}
                                  validate={validate(
                                    cardFields.membershipNumber.mandatory ? isRequired : noop,
                                    isMedicare ? validateCardNumber : noop,
                                    isLengthGreaterThanOrEqualTo(_get(cardFields, 'membershipNumber.minLength')),
                                    isLengthLessThanOrEqualTo(_get(cardFields, 'membershipNumber.maxLength', 99)),
                                    matchesRegex(
                                      _get(cardFields, 'membershipNumber.validation.regex'),
                                      _get(cardFields, 'membershipNumber.validation.message'),
                                    ),
                                  )}
                                />
                              </Columns.Column>
                            )}
                            {cardFields.cardRank && (
                              <Columns.Column spread={5}>
                                <Field
                                  component={InputField.Formik}
                                  inputProps={{
                                    maxLength: cardFields.cardRank.maxLength,
                                    onFocus: () =>
                                      this.setState({
                                        focused: 'rank',
                                      }),
                                    type: cardFields.cardRank.inputType === 'numeric' ? 'tel' : 'text',
                                  }}
                                  name={this.buildFieldName('cardRank')}
                                  label={`${_get(cardFields, 'cardRank.displayName') || 'Card rank'} ${
                                    !isRequired && !cardFields?.cardRank?.mandatory ? '(optional)' : ''
                                  }
                                `}
                                  validate={
                                    isDependant
                                      ? validate(
                                          isLengthGreaterThanOrEqualTo(_get(cardFields, 'cardRank.minLength')),
                                          isLengthLessThanOrEqualTo(_get(cardFields, 'cardRank.maxLength', 99)),
                                          matchesRegex(
                                            _get(cardFields, 'cardRank.validation.regex'),
                                            _get(cardFields, 'cardRank.validation.message'),
                                          ),
                                        )
                                      : validate(
                                          cardFields.cardRank.mandatory ? isRequired : noop,
                                          isLengthGreaterThanOrEqualTo(_get(cardFields, 'cardRank.minLength')),
                                          isLengthLessThanOrEqualTo(_get(cardFields, 'cardRank.maxLength', 99)),
                                          matchesRegex(
                                            _get(cardFields, 'cardRank.validation.regex'),
                                            _get(cardFields, 'cardRank.validation.message'),
                                          ),
                                        )
                                  }
                                />
                              </Columns.Column>
                            )}
                          </Columns>
                          {(cardFields.issueDay || cardFields.issueMonth || cardFields.issueYear) && (
                            <Field
                              width="100%"
                              component={SingleDateField.Formik}
                              name={this.buildFieldName('issueDate')}
                              label={`Issue date (${_getDateProperty({
                                day: cardFields?.issueDay,
                                month: cardFields?.issueMonth,
                                year: cardFields?.issueYear,
                                type: 'format',
                              })}) ${
                                !isRequired &&
                                !cardFields?.issueDay?.mandatory &&
                                !cardFields?.issueMonth?.mandatory &&
                                !cardFields?.issueYear?.mandatory
                                  ? '(optional)'
                                  : ''
                              }`}
                              mask={_getDateProperty({
                                day: cardFields.issueDay,
                                month: cardFields.issueMonth,
                                year: cardFields.issueYear,
                                type: 'mask',
                              })}
                              placeholder="DD/MM/YYYY"
                              disabled={isDependant}
                              onFocus={() =>
                                this.setState({
                                  focused: 'issueDate',
                                })
                              }
                              formType={'tel'}
                              validate={validate(
                                cardFields.issueDay.mandatory &&
                                  cardFields.issueMonth.mandatory &&
                                  cardFields.issueYear.mandatory
                                  ? isRequired
                                  : noop,
                                isValidDate,
                                isFutureDate,
                              )}
                            />
                          )}
                          {(cardFields.expiryDay || cardFields.expiryMonth || cardFields.expiryYear) && (
                            <Field
                              component={SingleDateField.Formik}
                              name={this.buildFieldName('expiryDate')}
                              label={`Expiry date (${_getDateProperty({
                                day: cardFields.expiryDay,
                                month: cardFields.expiryMonth,
                                year: cardFields.expiryYear,
                                type: 'format',
                              })})`}
                              mask={_getDateProperty({
                                day: cardFields.expiryDay,
                                month: cardFields.expiryMonth,
                                year: cardFields.expiryYear,
                                type: 'mask',
                              })}
                              placeholder="DD/MM/YYYY"
                              datePickerProps={{
                                disabledDays: {
                                  before: new Date(),
                                },
                              }}
                              onFocus={() =>
                                this.setState({
                                  focused: 'issueDate',
                                })
                              }
                              disabled={isDependant}
                              validate={validate(
                                cardFields.expiryDay.mandatory &&
                                  cardFields.expiryMonth.mandatory &&
                                  cardFields.expiryYear.mandatory
                                  ? isRequired
                                  : noop,
                                isValidDate,
                              )}
                            />
                          )}
                          {cardFields.cardIssueNumber && (
                            <Field
                              component={InputField.Formik}
                              inputProps={{
                                disabled: isDependant,
                                maxLength: cardFields.cardIssueNumber.maxLength,
                                onFocus: () =>
                                  this.setState({
                                    focused: 'issueNumber',
                                  }),
                                type: cardFields.cardIssueNumber.inputType === 'numeric' ? 'tel' : 'text',
                              }}
                              name={this.buildFieldName('cardIssueNumber')}
                              label={`${_get(cardFields, 'cardIssueNumber.displayName') || 'Issue no.'} ${
                                !isRequired && !cardFields?.cardIssueNumber?.mandatory ? '(optional)' : ''
                              }`}
                              validate={validate(
                                cardFields.cardIssueNumber.mandatory ? isRequired : noop,
                                isLengthGreaterThanOrEqualTo(_get(cardFields, 'cardIssueNumber.minLength')),
                                isLengthLessThanOrEqualTo(_get(cardFields, 'cardIssueNumber.maxLength', 99)),
                                matchesRegex(
                                  _get(cardFields, 'cardIssueNumber.validation.regex'),
                                  _get(cardFields, 'cardIssueNumber.validation.message'),
                                ),
                              )}
                            />
                          )}
                        </FieldStack>
                      </React.Fragment>
                    )}
                  </Box>
                )}
              </Box>
            );
          }}
        </Loads.Success>
      </Loads>
    );
  };
}
